<script setup lang="ts">
import { toRefs, computed, ref } from "vue";

export type MeasurementType = {
	day: string;
	computed_size: number;
	delta: number;
};

const props = defineProps<{
	data: MeasurementType[];
	hasMeasurements: boolean;
}>();

const { data } = toRefs(props);

const MAX_BARS = 52;

// Keep the most recent MAX_BARS points and left-pad with 0 when there are fewer,
// so the sparkline always renders a consistent number of bars.
// Most recent points, aligned to the bars: null entries are left-padding.
const points = computed(() => {
	const recent = data.value.slice(-MAX_BARS);
	const padding: (MeasurementType | null)[] = Array(
		Math.max(0, MAX_BARS - recent.length)
	).fill(null);
	return [...padding, ...recent];
});

const deltas = computed(() => points.value.map((point) => point?.delta ?? 0));
// Symmetric scale so positive and negative deltas use the same magnitude,
// keeping the zero baseline centered in the sparkline.
const maxAbsDelta = computed(() =>
	Math.ceil(Math.max(...deltas.value.map((d) => Math.abs(d))))
);

// Vertical gradient: with the symmetric scale, zero is at the vertical centre,
// so the top half colours positive bars and the bottom half negative bars.
// Each colour is repeated so the crossover at zero is a sharp edge, not a blend.
const POSITIVE_COLOR = "rgba(56, 142, 60, 0.7)";
const NEGATIVE_COLOR = "rgba(211, 47, 47, 0.7)";
const signGradient = [
	...Array(12).fill(POSITIVE_COLOR),
	...Array(12).fill(NEGATIVE_COLOR),
];

// A single tooltip per graph (instead of one per bar) keeps the table light.
// We track the hovered cell so the shared tooltip still anchors to that bar.
const hoveredEl = ref<HTMLElement | null>(null);
const hoveredPoint = ref<MeasurementType | null>(null);

const showTooltip = (point: MeasurementType | null, event: MouseEvent) => {
	if (!point || point.computed_size <= 0) {
		return;
	}
	hoveredPoint.value = point;
	hoveredEl.value = event.currentTarget as HTMLElement;
};

const hideTooltip = () => {
	hoveredPoint.value = null;
};

const tooltipText = computed(() =>
	hoveredPoint.value
		? `Δ ${hoveredPoint.value.delta} | Segment Size: ${hoveredPoint.value.computed_size}`
		: ""
);
</script>

<template>
	<div v-if="hasMeasurements && data.length > 1" class="chartContainer pa-1">
		<div class="sparklineWrapper">
			<span class="zeroLine" />
			<v-sparkline
				:model-value="deltas"
				:min="-maxAbsDelta"
				:max="maxAbsDelta"
				:gradient="signGradient"
				gradient-direction="top"
				type="bar"
				height="34"
				line-width="4"
				padding="2"
				:auto-draw="false"
			/>
			<div class="hoverCells">
				<div
					v-for="(point, index) in points"
					:key="index"
					class="hoverCell"
					@mouseenter="showTooltip(point, $event)"
					@mouseleave="hideTooltip"
				/>
			</div>
			<v-tooltip
				:model-value="!!hoveredPoint"
				:activator="hoveredEl"
				:open-on-hover="false"
				location="top"
				:text="tooltipText"
			/>
		</div>
	</div>
	<div class="chartContainer pa-1" v-else>
		<div
			class="sparklineWrapper"
			v-tooltip="
				`Historic segment size is automatically computed nightly (once per day).`
			"
		>
			<span class="zeroLine" />
			<span class="emptyLabel">Segment history not yet computed.</span>
		</div>
	</div>
</template>

<style scoped lang="scss">
.chartContainer {
	height: 50px;
	width: 100%;
}

.sparklineWrapper {
	position: relative;
	width: 100%;
	height: 34px;
}

.emptyLabel {
	position: absolute;
	top: 10px;
	right: 0;
	transform: translateY(-50%);
	font-size: 10px;
	line-height: 1;
	color: #999999;
	pointer-events: none;
}

.zeroLine {
	position: absolute;
	top: 20px;
	left: 0;
	width: 100%;
	height: 1px;
	background-color: #cccccc;
	pointer-events: none;
}

.hoverCells {
	position: absolute;
	inset: 0;
	display: flex;
}

.hoverCell {
	flex: 1 1 0;
	height: 100%;
}
</style>
